import { isEqual } from 'lodash';

import type { DtoV1PropertyTaxEscrowDetails } from '~/__generated__/api';

type DropDownOption = {
  label: string;
  value: number;
};

type GetInitialFormValuesParams = {
  pastTaxesPayTypeOptions: DropDownOption[];
  installmentsPayTypeOptions: Record<string, DropDownOption[]>;
  futureTaxesPayTypeOptions: Record<string, DropDownOption[]>;
  supplementalTaxesPayTypeOptions?: Record<string, DropDownOption[]>;
};

export const getInitialFormValues = (
  data: DtoV1PropertyTaxEscrowDetails,
  params?: GetInitialFormValuesParams,
) => {
  if (!data) return {} as never;
  let total = 0;
  let unequalInstallments = false;
  let unequalInstallmentInitialValue: number | undefined;

  const installments = (data?.escrowInstallments ?? []).map(
    ({ distributionDate, installmentAmount, paidType, discountDate }) => {
      unequalInstallmentInitialValue ??= installmentAmount;
      if (installmentAmount !== unequalInstallmentInitialValue) {
        unequalInstallments = true;
      }
      total += installmentAmount;
      const defaultPaidType =
        params?.installmentsPayTypeOptions?.[`installments.${0}.paidType`]?.[0]
          ?.value;
      return {
        discountDate,
        distributionDate,
        installmentAmount,
        paidType: paidType ?? defaultPaidType,
      };
    },
  );
  const [futureTaxes, pastTaxes] = [[], []];
  (data?.additionalTaxes ?? []).forEach(
    ({
      distributionDate,
      installmentAmount,
      isPastInstallment,
      paidType,
      installmentId,
      discountDate,
    }) => {
      const defaultPaidType = isPastInstallment
        ? params?.pastTaxesPayTypeOptions?.[0]?.value
        : params?.futureTaxesPayTypeOptions?.[`futureTaxes.${0}.paidType`]?.[0]
            ?.value;
      const installment = {
        distributionDate,
        installmentAmount: installmentAmount,
        paidType: paidType ?? defaultPaidType,
        installmentId,
        discountDate,
      };
      if (isPastInstallment) {
        pastTaxes.push(installment);
      } else {
        futureTaxes.push(installment);
      }
    },
  );
  const isMillageRate = !!data?.millageRate || !!data?.taxRate;

  const supplementalTaxes = (data?.supplementalInstallments ?? []).map(
    (
      { supplementalDate, supplementalAmount, paidType, supplementalId },
      index,
    ) => {
      return {
        distributionDate: supplementalDate ?? undefined,
        installmentAmount: supplementalAmount,
        paidType:
          paidType ??
          params?.supplementalTaxesPayTypeOptions?.[
            `supplementalTaxes.${index}.paidType`
          ]?.[0]?.value,
        supplementalId,
      };
    },
  );

  const includeSupplementalTaxes =
    data.includeSupplementals === true || supplementalTaxes.length > 0;

  return {
    unequalInstallments,
    isMillageRate,
    isNonAdValorem: data.nonAdValorum > 0 || false,
    installments,
    futureTaxes,
    nonAdValorem: data.nonAdValorum,
    pastTaxes,
    totalAmount: total,
    includeSupplementalTaxes,
    supplementalTaxes,
    isBorrowerExempt: Boolean(data?.isBorrowerExempt),
    millageRate: data?.millageRate ? String(data?.millageRate) : ' ',
    taxRate: !data?.millageRate && data?.taxRate ? String(data?.taxRate) : ' ',
    payeeName: data?.payeeName,
    additionalTaxesPaidAtClosing: data.additionalDueAtClosing ?? 0,
    delinquentTaxAmount: data.delinquentDueAtClosing ?? 0,
    supplementalDueAtClosing: data.supplementalDueAtClosing ?? 0,
    supplementalDueAtClosingId: data.supplementalDueAtClosingId,
  };
};

export type TaxesFormValues = ReturnType<typeof getInitialFormValues>;

export const isDirty = (values, initialValues) => {
  const initialInstallments = initialValues.installments ?? [];
  const currentInstallments = values.installments ?? [];
  const initialSupplementalTaxes = initialValues.supplementalTaxes ?? [];
  const currentSupplementalTaxes = values.supplementalTaxes ?? [];

  const areInstallmentsDirty = currentInstallments.some(
    (installment, index) => {
      const initialInstallment = initialInstallments[index];
      if (!initialInstallment) return true;
      return (
        !isEqual(
          installment.installmentAmount,
          initialInstallment.installmentAmount,
        ) || !isEqual(installment.paidType, initialInstallment.paidType)
      );
    },
  );
  const areSupplementalTaxesDirty =
    currentSupplementalTaxes.length !== initialSupplementalTaxes.length ||
    currentSupplementalTaxes.some((installment, index) => {
      const initialInstallment = initialSupplementalTaxes[index];
      if (!initialInstallment) return true;
      return (
        !isEqual(
          installment.distributionDate,
          initialInstallment.distributionDate,
        ) ||
        !isEqual(
          installment.installmentAmount,
          initialInstallment.installmentAmount,
        ) ||
        !isEqual(installment.paidType, initialInstallment.paidType)
      );
    });

  return (
    areInstallmentsDirty ||
    areSupplementalTaxesDirty ||
    !isEqual(
      values.includeSupplementalTaxes,
      initialValues.includeSupplementalTaxes,
    ) ||
    !isEqual(values.millageRate, initialValues.millageRate) ||
    !isEqual(values.delinquentTaxAmount, initialValues.delinquentTaxAmount) ||
    !isEqual(
      values.additionalTaxesPaidAtClosing,
      initialValues.additionalTaxesPaidAtClosing,
    ) ||
    !isEqual(
      values.supplementalDueAtClosing,
      initialValues.supplementalDueAtClosing,
    )
  );
};
